/**
 * StageStudyConfirmModal — confirms WHICH study a study-attributed status
 * (Screened / Randomized) applies to before the move is saved. The chosen study
 * rides the PATCH as primary_study_id in the same call, so the reporting ledger
 * freezes the transition under the RIGHT study (a lead who pivoted between
 * studies must never be counted under the one they came in for). The backend
 * rejects these moves without a study (reason 'study_confirmation_required').
 *
 * Bulk mode (bulkCount set): no per-lead picker is possible — the modal states
 * that each lead is recorded under its own current considering study and the
 * caller sends study_confirmed:true instead.
 */
import { useEffect, useState } from 'react';
import { lifecycleMeta } from '../lib';
import StudyPickField from './StudyPickField';
import { Z } from './ui';

interface Props {
  /** The study-attributed status being moved to (drives the title). */
  status: string;
  /** The lead's current considering study — preselected when known. */
  defaultStudyId?: string | null;
  /** Set for the bulk path: number of selected leads (hides the picker). */
  bulkCount?: number;
  /** Single mode: chosen study id. Bulk mode: called with no argument. */
  onConfirm: (studyId?: string) => void;
  onCancel: () => void;
}

export default function StageStudyConfirmModal({ status, defaultStudyId, bulkCount, onConfirm, onCancel }: Props) {
  const [studyId, setStudyId] = useState(defaultStudyId || '');
  const label = lifecycleMeta(status).label;
  const bulk = typeof bulkCount === 'number' && bulkCount > 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const confirm = () => {
    if (bulk) onConfirm();
    else if (studyId) onConfirm(studyId);
  };

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/40 p-4`}
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Confirm study for ${label}`}
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Mark as “{label}”
        </h2>
        {bulk ? (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {bulkCount} lead{bulkCount === 1 ? '' : 's'} will be recorded as “{label}”
            under <span className="font-medium text-gray-700 dark:text-gray-200">each lead’s own considering study</span>.
            If any of them pivoted to a different study, verify those leads
            individually so the by-study reports stay accurate.
          </p>
        ) : (
          <>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Confirm the study this outcome belongs to — by-study reports will
              count this lead there.
            </p>
            <StudyPickField autoFocus value={studyId} onChange={setStudyId} />
          </>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!bulk && !studyId}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save status
          </button>
        </div>
      </div>
    </div>
  );
}
